/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import { Injectable } from '@angular/core';
import { AbstractCanvasConnection } from './AbstractCanvasConnection';
import { IPromisePeriod, PromisePeriod } from '../../../app-data/models/PromisePeriod';
import { PromisePeriodList } from '../../../common/fancy-firebase/lists/PromisePeriodList';
import { PromisePeriodApi } from '../../../app-data/state/PromisePeriodApi';
import { PlanObject } from '../../../common/fancy-firebase/objects/PlanObject';
import { DefaultSplitDataRenderType } from '@touchplan/common';
import { Observable, Subject } from 'rxjs';
/**
 * Interface for the `IPromiseNowPosition` object to control visibility/positioning of the promise now button
 */
export interface IPromiseNowPosition {
	visible: boolean;
	x: number;
	y: number;
}

@Injectable({ providedIn: 'root' })
export class PromisePeriodService extends AbstractCanvasConnection<Map<string, PromisePeriod>> {
	/**
	 * Private subject containing the expected `Promise Now` position, in screen space.
	 */
	private _positionChanged$: Subject<IPromiseNowPosition> | null = new Subject<IPromiseNowPosition>();
	/**
	 * Public observable containing the Point data for the promise now button
	 */
	public position$: Observable<IPromiseNowPosition> | null =
		this._positionChanged$?.asObservable() as Subject<IPromiseNowPosition> | null;

	private _promisePeriodList!: PromisePeriodList;
	private _api!: PromisePeriodApi;
	private _plan!: PlanObject;
	/**
	 * Helper getter to get the ID of the current (next) promise period
	 */
	get currentPromisePeriodId(): string {
		return this._promisePeriodList.currentPromisePeriodId;
	}

	/**
	 * Helper getter to get the ID of the last (locked) promise period
	 */
	get lastPromisePeriodId(): string {
		return this._promisePeriodList.lockedPromisePeriodId;
	}
	/**
	 * Registers the service to receive promise period data from a `PromisePeriodList`
	 * @param fancyList PromisePeriodList object representing Promise Period List firebase data
	 * @param promisePeriodApi API for writing new data to firebase
	 * @param planObject Current plan object (use this to access the `Plan` to simplify API
	 */
	public override registerPlanData(
		fancyList: PromisePeriodList,
		promisePeriodApi: PromisePeriodApi,
		planObject: PlanObject
	): void {
		this._promisePeriodList = fancyList;
		this._api = promisePeriodApi;
		this._plan = planObject;

		super.registerPlanData();
		// This service is providedIn root, so there is no shutdownSubject/ngOnDestroy to hook into
		// Instead, it has been added to the PlanComponent destroy list and will be cleared up once the parent component is destroyed
		this._subscription = fancyList.list$.subscribe((data: Map<string, PromisePeriod>) =>
			this._changed$.next(data)
		);
	}
	/**
	 * Writes {minimized:true} for the PromisePeriod matching ID
	 * Automatically persists data to Firebase when invoked.
	 * @param id The ID of the Promise Period to modify
	 */
	public minimize(id: string): void {
		const period: PromisePeriod = this._promisePeriodList.syncList.get(id) as PromisePeriod;
		this._api.minimize(period);
	}
	/**
	 * Writes {minimized:false} for the PromisePeriod matching ID
	 * Automatically persists data to Firebase when invoked.
	 * @param id The ID of the Promise Period to modify
	 */
	public expand(id: string): void {
		const period: PromisePeriod = this._promisePeriodList.syncList.get(id) as PromisePeriod;
		this._api.expand(period);
	}
	/**
	 * Writes {locked:true} for the PromisePeriod matching ID.
	 * This will lock the provided promise period and then push a new period to the stack
	 * Automatically persists data to Firebase when invoked.
	 * @param id The ID of the Promise Period to modify
	 */
	public lock(id: string): void {
		this._api.lockPeriod(id, this._plan.syncObject);
	}
	/**
	 * Writes {locked:true} for the current promise period.
	 * This will lock the provided promise period and then push a new period to the stack
	 * Automatically persists data to Firebase when invoked.
	 * @param id The ID of the Promise Period to modify
	 */
	public lockFromCurrent(): void {
		this.lock(this._promisePeriodList.currentPromisePeriodId);
	}
	/**
	 * Toggle the minimized flag for the promise period matching ID
	 * Automatically persists data to Firebase when invoked.
	 * @param id The ID of the Promise Period to modify
	 */
	public toggleMinimize(id: string): void {
		this._api.toggleMinimize(id);
	}
	/**
	 * Switch to Edit View
	 */
	public switchViewToEdit(): void {
		this._api.changeView(this._promisePeriodList.syncList, DefaultSplitDataRenderType.edit);
	}
	/**
	 * Switch to Database View
	 */
	public switchViewToDatabase(): void {
		this._api.changeView(this._promisePeriodList.syncList, DefaultSplitDataRenderType.database);
	}
	/**
	 * Sets a new endDate value for the given promise period ID
	 * Does not persist automatically (Call save() to save to Firebase)
	 * @param id The ID of the promise period object to save
	 * @param endDate The data to set
	 */
	public setEndDate(id: string, endDate: string): void {
		this.update(id, { endDate });
	}
	/**
	 * Sets a new startDate value for the given promise period ID
	 * Does not persist automatically (Call save() to save to Firebase)
	 * @param id The ID of the promise period object to save
	 * @param endDate The data to set
	 */
	public setStartDate(id: string, startDate: string): void {
		this.update(id, { startDate });
	}
	/**
	 * Set the promise now button position
	 * @param x The (screen) X position to set the button too
	 * @param y The (screen) Y position to set the button too
	 * @param isVisible determines if the promise now button should be visible or now
	 */
	public setButtonPosition(x: number, y: number, isVisible: boolean = true): void {
		this._positionChanged$?.next({ x, y, visible: isVisible });
	}
	/**
	 * Destroy all the things
	 */
	public override destroy(): void {
		super.destroy();
		this._api.destroy();
		this._positionChanged$?.complete();
		this._positionChanged$ = null;
		this._plan = null;
		this._api = null;
		this._promisePeriodList = null;
	}
	/**
	 * Persist current data to Firebase
	 * @param renderType
	 */
	public save(renderType: string): void {
		this._api.persistData(this._promisePeriodList.syncList, renderType);
	}
	/**
	 * Private helper method to help update the local data state.
	 * @param id The ID of the object in the list to update
	 * @param data Partial<PromisePeriod> The data to update in the local state
	 * @private
	 */
	private update(id: string, data: Partial<IPromisePeriod>): void {
		this._api.localUpdate(id, data);
	}
}
